import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import ShippingAddressStep, {
  type ShippingAddress,
} from "@/components/store/ShippingAddressStep";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useStoreCart, formatPrice, type CartLine } from "@/store/storeCart";
import { useAuth } from "@/lib/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PLATFORM_FEE_BPS = 1000;

const StoreCartPage = () => {
  // Derive everything from `lines` to avoid unstable Zustand selectors
  // that return new object references on every call.
  const { user } = useAuth();
  const navigate = useNavigate();
  const lines = useStoreCart((s) => s.lines);
  const remove = useStoreCart((s) => s.remove);
  const setQuantity = useStoreCart((s) => s.setQuantity);

  // Derive from lines directly — calling store methods inside selectors
  // returns new object refs every time, causing infinite re-renders.
  const totalCents = useMemo(
    () => lines.reduce((sum, l) => sum + l.unit_price_cents * l.quantity, 0),
    [lines],
  );
  const vendorGroups = useMemo(() => {
    const groups: Record<string, CartLine[]> = {};
    for (const l of lines) (groups[l.vendor_id] ||= []).push(l);
    return groups;
  }, [lines]);
  const hasPhysical = useMemo(
    () => lines.some((l) => l.product_type === "physical"),
    [lines],
  );
  const platformFee = Math.floor((totalCents * PLATFORM_FEE_BPS) / 10000);

  const [submitting, setSubmitting] = useState(false);
  const [shipping, setShipping] = useState<ShippingAddress | null>(null);

  const checkout = async () => {
    if (!user) {
      toast.error("Sign in to check out", {
        action: { label: "Sign in", onClick: () => navigate("/auth?redirect=/store/cart") },
      });
      return;
    }
    if (hasPhysical && !shipping) {
      toast.error("Add a shipping address to continue");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("store-create-checkout", {
        body: {
          lines: lines.map((l) => ({
            product_id: l.product_id,
            variant_id: l.variant_id,
            quantity: l.quantity,
          })),
          shipping_address: hasPhysical ? shipping : null,
          success_url: `${window.location.origin}/store/checkout/success`,
          cancel_url: `${window.location.origin}/store/cart`,
        },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data?.error ?? "Checkout failed");
      }
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message ?? "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-5xl px-6 py-8">
        <h1 className="text-3xl font-bold">Your cart</h1>

        {lines.length === 0 ? (
          <Card className="p-12 text-center mt-8">
            <ShoppingBag className="h-12 w-12 text-muted-foreground/50 mx-auto" />
            <h2 className="mt-4 text-lg font-semibold">Your cart is empty</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Browse the storefront to find something you love.
            </p>
            <Link to="/store" className="inline-block mt-6">
              <Button>Browse the Store</Button>
            </Link>
          </Card>
        ) : (
          <div className="mt-8 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-8">
              {hasPhysical && user && (
                <ShippingAddressStep
                  selectedId={shipping?.id ?? null}
                  onSelect={setShipping}
                />
              )}
              {Object.entries(vendorGroups).map(([vendorId, vlines]) => {
                const vendor = vlines[0];
                return (
                  <div key={vendorId}>
                    <Link
                      to={`/store/vendor/${vendor.vendor_slug}`}
                      className="text-sm font-semibold text-muted-foreground hover:text-foreground"
                    >
                      Sold by {vendor.vendor_name}
                    </Link>
                    <div className="mt-3 space-y-3">
                      {vlines.map((l) => (
                        <Card key={`${l.product_id}-${l.variant_id ?? "none"}`} className="p-4 flex gap-4">
                          {l.image_url && (
                            <img
                              src={l.image_url}
                              alt=""
                              className="h-20 w-20 object-cover rounded bg-muted"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/store/product/${l.product_slug}`}
                              className="font-medium hover:underline truncate block"
                            >
                              {l.title}
                            </Link>
                            {l.variant_title && (
                              <p className="text-sm text-muted-foreground">{l.variant_title}</p>
                            )}
                            <p className="mt-1 text-sm font-semibold">
                              {formatPrice(l.unit_price_cents, l.currency)}
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() =>
                                  setQuantity(l.product_id, l.variant_id, l.quantity - 1)
                                }
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center text-sm">{l.quantity}</span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() =>
                                  setQuantity(l.product_id, l.variant_id, l.quantity + 1)
                                }
                                disabled={
                                  l.max_stock != null && l.quantity >= l.max_stock
                                }
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 ml-auto text-muted-foreground hover:text-destructive"
                                onClick={() => remove(l.product_id, l.variant_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="font-semibold whitespace-nowrap">
                            {formatPrice(l.unit_price_cents * l.quantity, l.currency)}
                          </p>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="md:col-span-1">
              <Card className="p-6 sticky top-20">
                <h2 className="font-semibold">Order summary</h2>
                <dl className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Subtotal</dt>
                    <dd className="font-medium">{formatPrice(totalCents)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Tax &amp; shipping</dt>
                    <dd className="text-muted-foreground">Calculated at checkout</dd>
                  </div>
                </dl>
                <hr className="my-4 border-border" />
                <div className="flex justify-between font-semibold">
                  <span>Estimated total</span>
                  <span>{formatPrice(totalCents)}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  One charge for all vendors. Wavlynq settles each vendor automatically.
                </p>
                <Button
                  className="w-full mt-4"
                  size="lg"
                  onClick={checkout}
                  disabled={submitting || (hasPhysical && !shipping)}
                >
                  {submitting
                    ? "Redirecting…"
                    : hasPhysical && !shipping
                      ? "Add shipping address"
                      : "Check out"}
                </Button>
                <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
                  Powered by Stripe. 14-day refund window. Refunds under $25 auto-approved.
                </p>
              </Card>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreCartPage;
